/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TaskComplete from '~/components/doc-preview/TaskComplete'
import { getTask } from '~/services/api/tasks'
import { Task } from '~/services/api/types'
import { toast } from '~/services/toast'
import Modal from '~/components/Modal'
import { useNavigate } from 'react-router'

interface TaskApproveModalProps {
  taskId: number
  task?: Task
  params?: Record<string, any>
  onClose: () => void
  onApprove?: () => void
}

/**
 * Task approval modal - Wrapper around TaskComplete component
 * Handles task loading and modal display with backdrop/overlay
 * Migrated from app/view/app/tasks/approve/taskApprove.modal.js
 */
function TaskApproveModal({ taskId, task: initialTask, params, onClose, onApprove }: TaskApproveModalProps) {
  const { t } = useTranslation()
  const [task, setTask] = useState<Task | null>(initialTask || null)
  const [isLoading, setIsLoading] = useState(!initialTask)
  const navigate = useNavigate()
  const isSimplified = !!params?.isSimplified

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden'

    // Load task if not provided
    if (!initialTask && taskId) {
      setIsLoading(true)
      getTask(String(taskId))
        .then((data) => {
          setTask(data)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error('Failed to load task:', error)
          // toast.error(t('FAILED_TO_LOAD_TASK_DETAILS'))
          setIsLoading(false)
          onClose()
        })
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [taskId, initialTask, t, onClose])

  const handleSave = () => {
    if (onApprove) {
      onApprove()
    }
    onClose()
  }

  const handleDismiss = (file?: any) => {
    onClose()
    if (file) {
      navigate(`show-file/${file.draftFsi.id}?version=${file.version}&minorVersion=${file.minorVersion}`)
    }
  }

  if (isLoading || !task) {
    return (
      <Modal show={true} handleClose={onClose} title={t('LOADING')} size="sm" draggable={false}>
        <div style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">{t('LOADING')}</span>
          </div>
        </div>
      </Modal>
    )
  }

  const translationParams = {
    name: task.name
  }

  return (
    <Modal
      show={true}
      handleClose={handleDismiss}
      title={t(!isSimplified ? 'TASKS_APPROVE_H_TITLE' : 'TASKS_APPROVE_TYPE_INFO_H_TITLE', translationParams)}
      size="md"
      className="theme-sandman"
      draggable={false}
    >
      <TaskComplete
        task={task}
        isModal={true}
        type={'taskComplete'}
        params={params}
        onApprove={handleSave}
        onCancel={handleDismiss}
      />
    </Modal>
  )
}

export default TaskApproveModal
